import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus, Loader2, Pencil, Trash2, Inbox,
  AlertCircle, Repeat, Calendar as CalIcon, CalendarRange,
} from 'lucide-react';

import { Button, Card } from '../../ui-v2';
import { getMisBloqueos, borrarBloqueo } from '../api';
import EditorBloqueo from './EditorBloqueo';

const DOW = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

export default function TabBloqueos() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editorTarget, setEditorTarget] = useState(null); // null o {bloqueo:null} para crear / {bloqueo:obj} para editar
  const [borrandoId, setBorrandoId] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getMisBloqueos();
      setData(res.data || []);
    } catch (e) {
      console.error('[TabBloqueos]', e);
      setError(e?.response?.data?.error || 'No se pudieron cargar tus bloqueos.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function pedirBorrar(id) {
    if (!window.confirm('¿Borrar este bloqueo?')) return;
    setBorrandoId(id);
    try {
      await borrarBloqueo(id);
      await cargar();
    } catch (e) {
      console.error('[borrarBloqueo]', e);
      alert(e?.response?.data?.error || 'No se pudo borrar el bloqueo.');
    } finally { setBorrandoId(null); }
  }

  if (loading) {
    return (
      <div className="py-12 text-center text-sm text-slate-500">
        <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
        Cargando…
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">
          Configura cuándo NO estás disponible para que el sistema no te asigne citas
          en esos momentos. Los bloqueos solo te afectan a ti.
        </p>
        <Button onClick={() => setEditorTarget({ bloqueo: null })} size="sm">
          <Plus className="w-4 h-4" />
          Nuevo bloqueo
        </Button>
      </div>

      {error && (
        <div className="mb-3 border border-red-200 bg-red-50 rounded-lg p-3 text-red-700 text-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {data.length === 0 ? (
        <Card className="p-12 text-center">
          <Inbox className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500">
            No tienes ningún bloqueo configurado.
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.map((b) => (
            <FilaBloqueo
              key={b.id}
              bloqueo={b}
              onEditar={() => setEditorTarget({ bloqueo: b })}
              onBorrar={() => pedirBorrar(b.id)}
              borrando={borrandoId === b.id}
            />
          ))}
        </div>
      )}

      {editorTarget && (
        <EditorBloqueo
          bloqueo={editorTarget.bloqueo}
          onClose={() => setEditorTarget(null)}
          onOk={async () => {
            setEditorTarget(null);
            await cargar();
          }}
        />
      )}
    </div>
  );
}

function FilaBloqueo({ bloqueo, onEditar, onBorrar, borrando }) {
  const { patron } = bloqueo;
  const Icon =
    patron === 'semanal' ? Repeat :
    patron === 'rango' ? CalendarRange :
    CalIcon;

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-slate-100 grid place-items-center shrink-0">
            <Icon className="w-4 h-4 text-slate-600" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-800">
              {describirBloqueo(bloqueo)}
            </div>
            {bloqueo.motivo && (
              <div className="text-xs text-slate-500 italic mt-0.5">
                {bloqueo.motivo}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onEditar}
            title="Editar"
            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"
          >
            <Pencil className="w-4 h-4 text-slate-600" />
          </button>
          <button
            onClick={onBorrar}
            disabled={borrando}
            title="Borrar"
            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-red-50 disabled:opacity-50"
          >
            {borrando ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            ) : (
              <Trash2 className="w-4 h-4 text-red-500" />
            )}
          </button>
        </div>
      </div>
    </Card>
  );
}

function describirBloqueo(b) {
  const horas = (b.hora_inicio && b.hora_fin)
    ? ` de ${b.hora_inicio} a ${b.hora_fin}`
    : ' (día entero)';

  if (b.patron === 'semanal') {
    return `Todos los ${DOW[b.dia_semana]}${horas}`;
  }
  if (b.patron === 'dia') {
    return `El día ${formatFecha(b.fecha_inicio)}${horas}`;
  }
  if (b.patron === 'rango') {
    return `Del ${formatFecha(b.fecha_inicio)} al ${formatFecha(b.fecha_fin)}${horas}`;
  }
  return JSON.stringify(b);
}

function formatFecha(s) {
  if (!s) return '?';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}
